import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Globe } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { comunidadeService, Comunidade } from '../../services/comunidadeService';

interface InstanciaOption {
  id: string;
  nome: string;
}

interface CriarComunidadeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  instancias: InstanciaOption[];
  instanciaPadraoId?: string;
  onCriada: (comunidade: Comunidade) => void;
}

export const CriarComunidadeDialog: React.FC<CriarComunidadeDialogProps> = ({
  open, onOpenChange, instancias, instanciaPadraoId, onCriada,
}) => {
  const { toast } = useToast();
  const [instanciaUazapiId, setInstanciaUazapiId] = useState(instanciaPadraoId || '');
  const [nome, setNome] = useState('');
  const [salvando, setSalvando] = useState(false);

  React.useEffect(() => {
    if (open) {
      setInstanciaUazapiId(instanciaPadraoId || instancias[0]?.id || '');
      setNome('');
    }
  }, [open, instanciaPadraoId, instancias]);

  const handleCriar = async () => {
    if (!instanciaUazapiId) {
      toast({ title: 'Aviso', description: 'Selecione uma instância', variant: 'destructive' });
      return;
    }
    if (!nome.trim()) {
      toast({ title: 'Aviso', description: 'Informe o nome da comunidade', variant: 'destructive' });
      return;
    }

    setSalvando(true);
    try {
      const response = await comunidadeService.criar({ instanciaUazapiId, nome: nome.trim() });
      toast({ title: 'Comunidade criada', description: `"${nome.trim()}" foi criada com sucesso.` });
      onCriada(response.data.comunidade);
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: 'Erro ao criar comunidade',
        description: error?.response?.data?.message || 'Não foi possível criar a comunidade.',
        variant: 'destructive',
      });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-white dark:bg-slate-800 dark:border-slate-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 dark:text-white">
            <Globe className="h-5 w-5" />
            Nova Comunidade
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label className="dark:text-gray-200" htmlFor="comunidade-instancia">Instância</Label>
            <Select value={instanciaUazapiId} onValueChange={setInstanciaUazapiId}>
              <SelectTrigger id="comunidade-instancia" className="dark:bg-slate-900 dark:border-slate-700">
                <SelectValue placeholder="Selecione a instância" />
              </SelectTrigger>
              <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                {instancias.map((inst) => (
                  <SelectItem key={inst.id} value={inst.id}>{inst.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="dark:text-gray-200" htmlFor="comunidade-nome">Nome da comunidade</Label>
            <Input className="dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:placeholder:text-gray-400"
              id="comunidade-nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: Comunidade de Clientes"
              maxLength={100}
            />
            <p className="text-xs text-muted-foreground dark:text-gray-400">
              A Uazapi cria automaticamente o grupo principal de anúncios. Depois, use "Gerenciar" para vincular grupos existentes.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={salvando}>Cancelar</Button>
          <Button onClick={handleCriar} disabled={salvando} className="bg-green-600 hover:bg-green-700">
            {salvando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Criar comunidade
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CriarComunidadeDialog;
